import { z } from 'zod';

// ---------------------------------------------------------------------------
// Our Quoted Brands: the brands in our quotation for a deal, one entry per system/package and brand,
// grouped by quotation revision. Brand values come from the org's central brand list.
// ---------------------------------------------------------------------------

export const QUOTE_SYSTEMS = [
  { value: 'cctv', label: 'CCTV' },
  { value: 'access_control', label: 'Access Control' },
  { value: 'structured_cabling', label: 'Structured Cabling' },
  { value: 'network_wifi', label: 'Network / Wi-Fi' },
  { value: 'av', label: 'AV' },
  { value: 'pa_va', label: 'PA / VA' },
  { value: 'grms', label: 'GRMS' },
  { value: 'bms', label: 'BMS' },
  { value: 'iptv', label: 'IPTV' },
  { value: 'telephony', label: 'Telephony' },
  { value: 'parking', label: 'Parking' },
  { value: 'data_center', label: 'Data Center' },
  { value: 'fire_alarm', label: 'Fire Alarm' },
  { value: 'other', label: 'Other' },
] as const;
export type QuoteSystem = (typeof QUOTE_SYSTEMS)[number]['value'];

export const OFFER_TYPES = [{ value: 'main', label: 'Main offer' }, { value: 'alternative', label: 'Alternative' }] as const;
export const BRAND_APPROVAL = [
  { value: 'not_submitted', label: 'Not submitted' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
] as const;

/** Technical readiness of a system: each item's status (Not required items don't count). */
export const READINESS_STATUSES = [
  { value: 'not_required', label: 'Not required', progress: null },
  { value: 'not_started', label: 'Not started', progress: 0 },
  { value: 'pending_info', label: 'Pending information', progress: 0.25 },
  { value: 'in_progress', label: 'In progress', progress: 0.5 },
  { value: 'submitted', label: 'Submitted', progress: 0.75 },
  { value: 'under_review', label: 'Under review', progress: 0.75 },
  { value: 'approved_with_comments', label: 'Approved with comments', progress: 0.9 },
  { value: 'approved', label: 'Approved', progress: 1 },
  { value: 'completed', label: 'Completed', progress: 1 },
  { value: 'rejected', label: 'Rejected', progress: 0 },
] as const;
export type ReadinessStatus = (typeof READINESS_STATUSES)[number]['value'];
/** The readiness items of a system; 'technical' is the consultant / technical approval of the line. */
export const READINESS_ITEMS = [
  { key: 'boq', column: 'boqStatus', label: 'BOQ review' },
  { key: 'design', column: 'designStatus', label: 'Design' },
  { key: 'drawings', column: 'drawingsStatus', label: 'Drawings' },
  { key: 'compliance', column: 'complianceStatus', label: 'Compliance statement' },
  { key: 'technical', column: 'technicalApproval', label: 'Technical / consultant approval' },
  { key: 'vendorQuote', column: 'vendorQuoteStatus', label: 'Vendor quotation' },
  { key: 'costing', column: 'costingStatus', label: 'Costing' },
] as const;
export type ReadinessItem = (typeof READINESS_ITEMS)[number]['key'];
const readinessStatus = z.preprocess((v) => (v === '' ? null : v), z.enum(READINESS_STATUSES.map((s) => s.value) as [ReadinessStatus, ...ReadinessStatus[]]).nullable()).optional();

const optText = (max: number) => z.preprocess((v) => (typeof v === 'string' && v.trim() === '' ? null : v), z.string().trim().max(max).nullable()).optional();

export const brandLineSchema = z
  .object({
    system: z.enum(QUOTE_SYSTEMS.map((s) => s.value) as [QuoteSystem, ...QuoteSystem[]]),
    /** A value of the central brand list. */
    brand: z.string().trim().min(1, 'Choose a brand').max(80),
    offerType: z.enum(['main', 'alternative']).default('main'),
    /** An existing quotation of this deal (its number becomes the reference). */
    quoteId: z.string().uuid().nullable().optional(),
    quoteRef: optText(80),
    revision: z.string().trim().min(1).max(40).optional(),
    technicalApproval: z.enum(['not_submitted', 'pending', 'approved', 'rejected']).default('not_submitted'),
    /** Value of this system/package, when known (used for pipeline by brand and system). */
    packageValue: z.number().finite().min(0).max(1e13).nullable().optional(),
    notes: optText(2000),
    /** Why this brand is offered (older entries: why a brand other than the specified one was offered). */
    deviationReason: optText(1000),
    /** Suggested entries stay unconfirmed until pre-sales confirms them. */
    confirmed: z.boolean().default(true),
    /** Gross profit of this system in the quotation, %. */
    gpPct: z.number().finite().min(-100).max(100).nullable().optional(),
    boqStatus: readinessStatus,
    designStatus: readinessStatus,
    drawingsStatus: readinessStatus,
    complianceStatus: readinessStatus,
    vendorQuoteStatus: readinessStatus,
    costingStatus: readinessStatus,
    clarificationsOpen: z.number().int().min(0).max(999).optional(),
    readinessNotes: optText(2000),
  })
  .strict();
export const brandLineUpdateSchema = brandLineSchema.partial().strict();

export interface BrandLine {
  id: string;
  opportunityId: string;
  system: QuoteSystem;
  brand: string;
  offerType: 'main' | 'alternative';
  quoteId: string | null;
  quoteRef: string | null;
  revision: string;
  technicalApproval: 'not_submitted' | 'pending' | 'approved' | 'rejected';
  packageValue: number | null;
  notes: string | null;
  deviationReason: string | null;
  confirmed: boolean;
  gpPct: number | null;
  boqStatus: ReadinessStatus | null;
  designStatus: ReadinessStatus | null;
  drawingsStatus: ReadinessStatus | null;
  complianceStatus: ReadinessStatus | null;
  vendorQuoteStatus: ReadinessStatus | null;
  costingStatus: ReadinessStatus | null;
  clarificationsOpen: number;
  readinessNotes: string | null;
  source: 'manual' | 'suggested' | 'costing';
  version: number;
  createdAt: string;
  createdByName: string;
  updatedAt: string;
  updatedByName: string;
}

export interface BrandLinesResult {
  lines: BrandLine[];
  /** Revision shown as current: the deal's chosen one, else the latest. */
  currentRevision: string | null;
  revisions: string[];
  canEdit: boolean;
  /** Brands of the deal's quotation products that aren't recorded yet (brand list keys). */
  suggestions: { brand: string; quoteId: string | null; quoteRef: string | null }[];
}

/** Lines that count: confirmed entries of the current revision. */
export function currentLines<T extends { revision: string; confirmed: boolean }>(lines: readonly T[], current: string | null): T[] {
  return lines.filter((l) => l.confirmed && l.revision === current);
}

/**
 * Splits a deal's value over its main-offer entries without counting it twice: entries with a package
 * value keep it; the rest of the deal value is shared equally by the entries without one. Alternatives
 * carry no value (they replace a main offer).
 */
export function allocateDealValue<T extends { offerType: string; packageValue: number | null }>(amount: number | null, lines: readonly T[]): { line: T; value: number }[] {
  const main = lines.filter((l) => l.offerType === 'main');
  const known = main.reduce((n, l) => n + (l.packageValue ?? 0), 0);
  const open = main.filter((l) => l.packageValue == null);
  const share = open.length ? Math.max(0, (amount ?? 0) - known) / open.length : 0;
  return main.map((line) => ({ line, value: line.packageValue ?? share }));
}
